import React, { useState, useEffect, useRef } from 'react';
import {
  Home, Building2, Building, Layers, Trees, Map, Warehouse, Store,
  ChevronDown, Plus, X, RefreshCw, Save, StickyNote, Loader2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getContactBuyerPreferences,
  updateContactBuyerPreferences,
  suggestBuyerPreferencesFromNotes,
} from '../../../api/contact';
// Shared suburb reference list (single source of truth, includes Bellfield +
// the Banyule/Heidelberg cluster) so this editor suggests the same suburbs as
// the Find Buyers picker instead of forcing agents to free-type.
import { SUBURB_REGIONS } from '../../analytics/components/BuyerPreferenceFields';

// Canonical Property Goals ("What is most important to them?"), keep in sync
// with the buyer portal (Buyer-Portal-FE/src/utils/investmentGoals.js) and the
// backend BuyingPreference.INVESTMENT_GOAL_CHOICES.
// The ids are the STORED values, so a rename only ever touches the label:
// 'LAND_BANKING' kept its id when its label became "Land Banking".
const INVESTMENT_GOAL_OPTIONS = [
  { id: 'TO_LIVE_IN', label: 'To Live In' },
  { id: 'CAPITAL_GROWTH', label: 'Capital Growth' },
  { id: 'HIGH_RENTAL_YIELD', label: 'High Rental Yield' },
  { id: 'CASH_FLOW_POSITIVE_GEARING', label: 'Cash Flow / Positive Gearing' },
  { id: 'DEVELOPMENT_POTENTIAL', label: 'Development Potential' },
  { id: 'RENOVATION_POTENTIAL', label: 'Do-Up / Renovation' },
  { id: 'LAND_BANKING', label: 'Land Banking' },
  { id: 'SMSF', label: 'SMSF' },
  { id: 'COMMERCIAL_PROPERTY', label: 'Commercial Property' },
];

const PROPERTY_TYPES = [
  { id: 'Apartment', label: 'Apartment', icon: Building2 },
  { id: 'Unit', label: 'Unit', icon: Building2 },
  { id: 'House', label: 'House', icon: Home },
  { id: 'Townhouse', label: 'Townhouse', icon: Building },
  { id: 'Villa', label: 'Villa', icon: Warehouse },
  { id: 'House and Land', label: 'House and Land', icon: Map },
  { id: 'Land', label: 'Land', icon: Trees },
  { id: 'Block of Units', label: 'Block of Units', icon: Layers },
  { id: 'Commercial', label: 'Commercial', icon: Store },
];

// Commercial buyers care about a different spec than residential — the form
// swaps Bedrooms/Bathrooms/Car Spaces for Floor Area when 'Commercial' is chosen.
const COMMERCIAL_TYPE = 'Commercial';

const POPULAR_CITIES = ['Melbourne', 'Sydney', 'Brisbane', 'Gold Coast', 'Canberra', 'Townsville', 'Perth', 'Adelaide'];

const POPULAR_SUBURBS = [
  'Northcote', 'Brunswick', 'Fitzroy', 'Richmond', 'South Yarra', 'Preston',
  'Camberwell', 'Brighton', 'Footscray', 'Parramatta', 'Bondi', 'Chatswood',
  'Fortitude Valley', 'New Farm', 'Subiaco', 'Glenelg',
];

const BED_OPTIONS = ['Any', '1', '2', '3', '4', '5', '6+'];
const BATH_OPTIONS = ['Any', '1+', '2+', '3+', '4+', '5+', '6+'];
const CAR_OPTIONS = ['Any', '1+', '2+', '3+', '4+', '5+', '6+'];
// '' is "Not Specified" - a blank buying_timeline means not specified (ticket
// 13058739730), so a buyer is no longer defaulted to an urgent "0-3 months".
const TIMELINE_OPTIONS = [
  { value: '', label: 'Not Specified' },
  '0-3 months', '3-6 months', '6-12 months', '12+ months',
];
// 1500m² added at the client's request (CG, 2026-08-24), the list used to jump
// 1000 straight to 2000.
const LAND_OPTIONS = ['Any', '200m²', '300m²', '400m²', '500m²', '600m²', '700m²', '800m²', '900m²', '1000m²', '1500m²', '2000m²', '4000m²', '5000m²+'];
const FLOOR_AREA_OPTIONS = ['Any', '50m²', '100m²', '150m²', '200m²', '300m²', '400m²', '500m²', '750m²', '1000m²', '2000m²', '5000m²+'];

// Mirrors realestate.com.au's own Price filter brackets (realestate.com.au/buy/
// > Filters > Price) so a buyer's saved range lines up with what they'd pick
// searching there directly - the old list jumped straight from "Any" to
// $100,000 then $200,000, missing the low end REA covers in $25k steps in
// this range (CG report, 2026-08-24: "missing some crucial low numbers").
const PRICE_BRACKETS = [
  'Any', '$50,000', '$75,000', '$100,000', '$125,000', '$150,000', '$175,000', '$200,000', '$225,000', '$250,000',
  '$275,000', '$300,000', '$325,000', '$350,000', '$375,000', '$400,000', '$425,000', '$450,000', '$475,000', '$500,000',
  '$550,000', '$600,000', '$650,000', '$700,000', '$750,000', '$800,000', '$850,000', '$900,000', '$950,000', '$1,000,000',
  '$1,100,000', '$1,200,000', '$1,300,000', '$1,400,000', '$1,500,000', '$1,600,000', '$1,700,000', '$1,800,000', '$1,900,000', '$2,000,000',
  '$2,250,000', '$2,500,000', '$2,750,000', '$3,000,000', '$3,500,000', '$4,000,000', '$4,500,000', '$5,000,000',
  '$6,000,000', '$7,000,000', '$8,000,000', '$9,000,000', '$10,000,000', '$12,000,000', '$15,000,000',
];
const PRICE_MIN_OPTIONS = PRICE_BRACKETS;
const PRICE_MAX_OPTIONS = [...PRICE_BRACKETS.slice(0, -1), '$15,000,000+'];

// Canonical Buyer Context ("What best describes them?"), keep in sync with the
// portal and BuyingPreference.BUYER_TYPE_CHOICES. `id` is the value stored on
// the preference row, `label` is what the agent sees, so rows already saved as
// 'First home buyer' still tick under the "First Home Buyer" label.
const BUYER_TYPE_OPTIONS = [
  { id: 'First home buyer', label: 'First Home Buyer' },
  { id: 'Upsizer', label: 'Upsizer' },
  { id: 'Downsizer', label: 'Downsizer' },
  { id: 'Investor', label: 'Investor' },
  { id: 'Owner occupier', label: 'Owner Occupier' },
  { id: 'Rentvestor', label: 'Rentvestor' },
  { id: 'Developer', label: 'Developer' },
  { id: 'Relocating', label: 'Relocating' },
];
const FINANCE_OPTIONS = [
  'Cash buyer', 'Not ready yet', 'Pre-approved', 'Speaking with a broker', 'Need to speak with a broker',
];

const toTitleCase = (str) =>
  (str || '').toLowerCase().split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

// A preference's price can reach us either already formatted ("$900,000", set via
// this form or the portal) or as a raw number ("895000", written by the open-home
// seeding and the Find Buyers editor). The dropdowns only list formatted steps, so
// a raw number matches nothing and the field silently falls back to "Any". Format
// raw numbers to currency so the seeded value actually shows up.
const formatMoney = (val) => {
  if (val === null || val === undefined) return 'Any';
  const s = String(val).trim();
  if (!s || s.toLowerCase() === 'any') return 'Any';
  if (s.startsWith('$')) return s;
  const digits = s.replace(/[^\d.]/g, '');
  if (!digits) return s;
  const n = Math.round(parseFloat(digits));
  if (!Number.isFinite(n)) return s;
  return `$${n.toLocaleString('en-AU')}`;
};

// Bathrooms / Car Spaces are stored as a bare minimum ("2") but the dropdowns use
// the "+" form ("2+"). Map a plain number onto its "+" option so it displays.
const toPlusOption = (val, options) => {
  const s = (val === null || val === undefined) ? '' : String(val).trim();
  if (!s || s.toLowerCase() === 'any') return 'Any';
  if (options.includes(s)) return s;
  if (/^\d+$/.test(s) && options.includes(`${s}+`)) return `${s}+`;
  return s;
};

// Australian state names/abbreviations, longest first so multi-word names match
// before their abbreviations.
const AU_STATE_SUFFIXES = [
  'Australian Capital Territory', 'New South Wales', 'Northern Territory',
  'South Australia', 'Western Australia', 'Queensland', 'Tasmania', 'Victoria',
  'ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA',
];

// Some property records carry the state inside the suburb field
// ("Heidelberg Heights Victoria"), which the open-home seeding then copied into
// buyers' preferences as a bogus one-off suburb. Strip a trailing state so the
// chip reads as the suburb only — but never turn a value that IS just a state
// (a deliberate whole-state preference) into an empty string.
const stripStateSuffix = (val) => {
  const s = (val || '').trim();
  if (!s) return '';
  for (const st of AU_STATE_SUFFIXES) {
    const re = new RegExp(`[\\s,]+${st}$`, 'i');
    if (re.test(s)) {
      const candidate = s.replace(re, '').trim();
      if (candidate) return candidate;
    }
  }
  return s;
};

// Build the suburb autocomplete list: a free-type "Add" entry first, then any
// matching known suburbs/cities/states from the shared reference list.
const buildSuburbSuggestions = (q) => {
  const query = (q || '').trim().toLowerCase();
  if (query.length < 2) return [];
  const seen = new Set();
  const matches = [];
  for (const r of SUBURB_REGIONS) {
    if (r.name.toLowerCase().includes(query) && !seen.has(r.name.toLowerCase())) {
      seen.add(r.name.toLowerCase());
      matches.push(r);
    }
  }
  const rank = { locality: 1, city: 2, state: 3 };
  matches.sort((a, b) => {
    const ax = a.name.toLowerCase() === query ? 0 : 1;
    const bx = b.name.toLowerCase() === query ? 0 : 1;
    if (ax !== bx) return ax - bx; // exact-name match first
    return (rank[a.type] || 4) - (rank[b.type] || 4);
  });
  // Only offer the free-type "Add X" when the typed value isn't already a known
  // suburb — otherwise the recognised suburb (with its badge) is the suggestion.
  const out = [];
  if (!seen.has(query)) out.push({ name: toTitleCase(query), type: 'manual', isManual: true });
  out.push(...matches);
  return out.slice(0, 8);
};

// Clean + de-duplicate (case-insensitive) a stored suburb list on load.
const cleanSuburbs = (list) => {
  const seen = new Set();
  const out = [];
  for (const raw of (list || [])) {
    const s = stripStateSuffix(raw);
    if (s && !seen.has(s.toLowerCase())) {
      seen.add(s.toLowerCase());
      out.push(s);
    }
  }
  return out;
};

const EMPTY = {
  propertyType: [],
  priceMin: 'Any',
  priceMax: 'Any',
  bedroomsMin: 'Any',
  bedroomsMax: 'Any',
  bathrooms: 'Any',
  carSpaces: 'Any',
  landSizeMin: 'Any',
  landSizeMax: 'Any',
  floorAreaMin: 'Any',
  floorAreaMax: 'Any',
  buyingTimeline: '',
  buyerType: [],
  financeStatus: [],
  investmentGoals: [],
  suburbs: [],
};

const mapToFrontend = (data) => {
  if (!data || Object.keys(data).length === 0) return { ...EMPTY };
  let propertyTypeArray = [];
  if (Array.isArray(data.property_type)) propertyTypeArray = data.property_type;
  else if (data.property_type && data.property_type !== 'Any') propertyTypeArray = [data.property_type];
  return {
    propertyType: propertyTypeArray,
    priceMin: formatMoney(data.price_min),
    priceMax: formatMoney(data.price_max),
    bedroomsMin: data.bedrooms_min || 'Any',
    bedroomsMax: data.bedrooms_max || 'Any',
    bathrooms: toPlusOption(data.bathrooms, BATH_OPTIONS),
    carSpaces: toPlusOption(data.car_spaces, CAR_OPTIONS),
    landSizeMin: data.land_size_min || 'Any',
    landSizeMax: data.land_size_max || 'Any',
    floorAreaMin: data.floor_area_min || 'Any',
    floorAreaMax: data.floor_area_max || 'Any',
    buyingTimeline: data.buying_timeline || '',
    buyerType: data.buyer_type || [],
    financeStatus: data.finance_status || [],
    investmentGoals: data.investment_goals || [],
    suburbs: cleanSuburbs(data.suburbs),
  };
};

/**
 * Turn a failed preferences request into something an agent can act on. A bare
 * "Failed to save buyer preferences" told us nothing when this was reported
 * (CRM-2026-00059) — the reason was a 404 from the ownership check, invisible
 * without the browser console.
 */
const firstFieldError = (data) => {
  // DRF's raise_exception answers 400 with { field: ["message"] } and no
  // 'detail', so name the field that was rejected instead of a bare code.
  if (!data || typeof data !== 'object') return '';
  const entry = Object.entries(data).find(([, v]) => Array.isArray(v) ? v.length : typeof v === 'string');
  if (!entry) return '';
  const [field, value] = entry;
  return `${field}: ${Array.isArray(value) ? value[0] : value}`;
};

const saveErrorMessage = (error, fallback) => {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const detail = data?.detail || data?.error || firstFieldError(data);
  if (status === 404) return `${fallback} — this buyer's record could not be reached. Please report it.`;
  if (status === 403) return `${fallback} — you don't have permission to edit this buyer.`;
  if (status === 400 && detail) return `${fallback}: ${detail}`;
  if (!error?.response) return `${fallback} — no response from the server. Check your connection and try again.`;
  return detail ? `${fallback}: ${detail}` : `${fallback} (error ${status || 'unknown'})`;
};

const mapToBackend = (data) => ({
  property_type: data.propertyType,
  price_min: data.priceMin,
  price_max: data.priceMax,
  bedrooms_min: data.bedroomsMin,
  bedrooms_max: data.bedroomsMax,
  bathrooms: data.bathrooms,
  car_spaces: data.carSpaces,
  land_size_min: data.landSizeMin,
  land_size_max: data.landSizeMax,
  floor_area_min: data.floorAreaMin,
  floor_area_max: data.floorAreaMax,
  buying_timeline: data.buyingTimeline,
  buyer_type: data.buyerType,
  finance_status: data.financeStatus,
  investment_goals: data.investmentGoals,
  suburbs: data.suburbs,
});

// Backend preference field -> this form's key, for "Fill from notes".
const NOTE_FIELD_TO_FORM = {
  price_min: 'priceMin',
  price_max: 'priceMax',
  suburbs: 'suburbs',
  property_type: 'propertyType',
  bedrooms_min: 'bedroomsMin',
  bedrooms_max: 'bedroomsMax',
  bathrooms: 'bathrooms',
  car_spaces: 'carSpaces',
  land_size_min: 'landSizeMin',
  buying_timeline: 'buyingTimeline',
  buyer_type: 'buyerType',
  finance_status: 'financeStatus',
  investment_goals: 'investmentGoals',
};

// Put the note suggestions into the form. List fields only ever gain entries
// (the agent may have ticked something since the form loaded), and the
// Commercial/residential split is left alone, as togglePropertyType keeps it.
export const applyNoteChanges = (form, changes) => {
  const next = { ...form };
  (changes || []).forEach(({ field, value }) => {
    const key = NOTE_FIELD_TO_FORM[field];
    if (!key) return;
    if (Array.isArray(next[key])) {
      const have = new Set(next[key].map((v) => String(v).toLowerCase()));
      let add = (Array.isArray(value) ? value : [value]).filter((v) => !have.has(String(v).toLowerCase()));
      if (key === 'propertyType' && next[key].length) {
        const commercialNow = next[key].includes(COMMERCIAL_TYPE);
        add = add.filter((v) => (v === COMMERCIAL_TYPE) === commercialNow);
      }
      next[key] = [...next[key], ...add];
    } else if (value !== undefined && value !== null) {
      next[key] = value;
    }
  });
  return next;
};

const Dropdown = ({ name, value, options, onChange }) => {
  // A stored value that isn't one of the fixed steps (e.g. a seeded "$895,000")
  // would otherwise not be selectable and the field would show blank/"Any".
  // Surface it as its own option so it displays and round-trips on save.
  const normalized = options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
  const values = normalized.map((o) => o.value);
  const opts = (value && value !== 'Any' && !values.includes(value))
    ? [{ value, label: value }, ...normalized]
    : normalized;
  return (
  <div className="relative">
    <select
      name={name}
      value={value}
      onChange={onChange}
      className="w-full h-[48px] px-4 rounded-xl border border-gray-300 bg-white text-[14px] font-medium appearance-none cursor-pointer focus:outline-none focus:border-[#55C5D0] focus:ring-2 focus:ring-[#55C5D0]/10 transition-all"
    >
      {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
    <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
  </div>
  );
};

const Chip = ({ label, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={selected}
    className={`px-3 sm:px-4 py-2 rounded-lg text-[12px] sm:text-[13px] font-bold transition-all ${
      selected
        ? 'bg-[#E0F7FA] text-[#00838F] border border-[#55C5D0] cursor-default'
        : 'bg-white border border-gray-300 text-gray-600 hover:border-[#55C5D0] hover:bg-[#F0FDFE]'
    }`}
  >
    {label}
  </button>
);

const CheckOption = ({ checked, onChange, label }) => (
  <label className="flex items-center gap-3 cursor-pointer group">
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="w-5 h-5 rounded border-gray-300 text-[#55C5D0] focus:ring-[#55C5D0] cursor-pointer"
    />
    <span className="text-[14px] text-gray-600 font-medium group-hover:text-gray-900 transition-colors">{label}</span>
  </label>
);

/**
 * Agent-side Buying Preferences editor — mirrors the buyer-portal form. Reads the
 * raw BuyingPreference for the contact and writes it back through to the portal
 * record (which feeds buyer-trend stats). Calls onSaved(buyer_preferences) with
 * the rebuilt CRM snapshot so the parent can refresh its view.
 *
 * Bulk mode: pass `onBulkSave` (and no `contactId`) to reuse this exact form as a
 * "set these preferences on N people" editor. It then starts blank instead of
 * loading anybody's saved values, and Save hands the payload to the parent rather
 * than writing it to one contact. Used by the Database page's bulk bar, so agents
 * capture a bulk preference set through the same form they already know
 * (Monday DOMA 12840979019).
 *
 * `beforeSave` (optional, async): runs first on a single-contact Save; resolve
 * false to stop. The modals use it to save the search status picked above this
 * form, so Save Preferences saves everything the agent changed (CRM-2026-00134).
 */
const ContactBuyerPreferencesEditor = ({ contactId, onSaved, onBulkSave, bulkLabel, saveLabel, beforeSave }) => {
  const bulk = typeof onBulkSave === 'function';
  const [loading, setLoading] = useState(!bulk);
  const [saving, setSaving] = useState(false);
  const [portalUserFound, setPortalUserFound] = useState(false);
  const [form, setForm] = useState({ ...EMPTY });
  const [suburbInput, setSuburbInput] = useState('');
  const [suburbSuggestions, setSuburbSuggestions] = useState([]);
  const [suburbOpen, setSuburbOpen] = useState(false);
  const suburbBoxRef = useRef(null);
  // "Fill from notes" (Monday 13391227323): reading | what was filled, until Save.
  const [noteFill, setNoteFill] = useState(null);

  // Close the suggestion dropdown when clicking outside the picker.
  useEffect(() => {
    const away = (e) => {
      if (suburbBoxRef.current && !suburbBoxRef.current.contains(e.target)) setSuburbOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, []);

  useEffect(() => {
    // Bulk mode has no single contact to read from: the form starts blank and
    // only the fields the agent fills are applied.
    if (bulk) return undefined;
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const res = await getContactBuyerPreferences(contactId);
        if (!active) return;
        setPortalUserFound(!!res.data?.portal_user_found);
        setForm(mapToFrontend(res.data?.preference));
      } catch (e) {
        console.error('Failed to load buying preferences:', e);
        // Say so rather than silently presenting a blank form: an agent who
        // can't tell the load failed will fill it in and either lose the work
        // on save or overwrite real preferences with defaults.
        if (active) {
          setForm({ ...EMPTY });
          toast.error(saveErrorMessage(e, "Couldn't load this buyer's preferences"));
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [contactId, bulk]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const toggleInArray = (field, value) =>
    setForm((prev) => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter((v) => v !== value)
        : [...prev[field], value],
    }));

  // Commercial is mutually exclusive with residential types: picking it clears
  // the rest, and picking a residential type clears Commercial. This keeps the
  // conditional field set (residential specs vs Floor Area) unambiguous.
  const togglePropertyType = (id) =>
    setForm((prev) => {
      const selected = prev.propertyType.includes(id);
      if (id === COMMERCIAL_TYPE) {
        return { ...prev, propertyType: selected ? [] : [COMMERCIAL_TYPE] };
      }
      const withoutCommercial = prev.propertyType.filter((v) => v !== COMMERCIAL_TYPE);
      return {
        ...prev,
        propertyType: selected
          ? withoutCommercial.filter((v) => v !== id)
          : [...withoutCommercial, id],
      };
    });

  const isCommercial = form.propertyType.includes(COMMERCIAL_TYPE);

  const addSuburb = (raw) => {
    const val = stripStateSuffix(toTitleCase((raw ?? suburbInput).trim()));
    if (val && !form.suburbs.some((s) => s.toLowerCase() === val.toLowerCase())) {
      setForm((prev) => ({ ...prev, suburbs: [...prev.suburbs, val] }));
    }
    setSuburbInput('');
    setSuburbSuggestions([]);
    setSuburbOpen(false);
  };

  const onSuburbType = (v) => {
    setSuburbInput(v);
    const s = buildSuburbSuggestions(v);
    setSuburbSuggestions(s);
    setSuburbOpen(s.length > 0);
  };

  const removeSuburb = (s) =>
    setForm((prev) => ({ ...prev, suburbs: prev.suburbs.filter((x) => x !== s) }));

  // Read the buyer's notes and put any budget, suburbs or must-haves they
  // mention into the form. Nothing is saved until the agent presses Save.
  const fillFromNotes = async () => {
    setNoteFill({ reading: true });
    try {
      const res = await suggestBuyerPreferencesFromNotes(contactId);
      const { available, changes = [], notes_read: notesRead = 0 } = res.data || {};
      if (available === false) {
        setNoteFill(null);
        toast.error("Couldn't read the notes right now. Please try again shortly.");
        return;
      }
      if (!changes.length) {
        setNoteFill(null);
        toast(notesRead ? 'Nothing new in the notes to add to these preferences.' : 'This buyer has no notes yet.');
        return;
      }
      setForm((prev) => applyNoteChanges(prev, changes));
      setNoteFill({ changes });
    } catch (e) {
      setNoteFill(null);
      toast.error(saveErrorMessage(e, "Couldn't read this buyer's notes"));
    }
  };

  const handleSave = async () => {
    if (bulk) {
      // Only send what the agent actually set — an untouched field must stay
      // untouched on every selected person, because a stated bound is a hard gate
      // in buyer matching and a blanket 'Any' would wipe real preferences.
      const payload = {};
      const raw = mapToBackend(form);
      Object.entries(raw).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          if (value.length) payload[key] = value;
        } else if (value && String(value) !== 'Any') {
          payload[key] = value;
        }
      });
      // Timeline is blank ("Not Specified") until chosen, so the truthy check
      // above already keeps an unset timeline out of a bulk apply - a blank must
      // never wipe each person's stated timeframe.
      if (!Object.keys(payload).length) {
        toast.error('Set at least one preference to apply.');
        return;
      }
      try {
        setSaving(true);
        await onBulkSave(payload);
      } finally {
        setSaving(false);
      }
      return;
    }
    try {
      setSaving(true);
      // A failed status save keeps the modal open with the agent's pick intact.
      if (beforeSave && !(await beforeSave())) return;
      const res = await updateContactBuyerPreferences(contactId, mapToBackend(form));
      if (res.data?.preference) setForm(mapToFrontend(res.data.preference));
      setNoteFill(null);
      toast.success('Buyer preferences saved');
      onSaved?.(res.data?.buyer_preferences);
    } catch (e) {
      console.error('Failed to save buying preferences:', e);
      toast.error(saveErrorMessage(e, 'Failed to save buyer preferences'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#55C5D0]"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8 space-y-8">
      {/* Portal account status — or, in bulk mode, who this is about to apply to. */}
      {bulk ? (
        <div className="flex items-start gap-3 p-4 bg-[#F0FDFE] rounded-2xl border border-[#B7E9EE]">
          <div className="w-3 h-3 rounded-full bg-[#55C5D0] mt-1 shrink-0"></div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-[#00838F]">{bulkLabel || 'Applying to the selected contacts'}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Only the fields you set below are applied. Anything left on
              &quot;Any&quot; is left exactly as it is on each person.
            </p>
          </div>
        </div>
      ) : portalUserFound ? (
        // "No Portal Account Matched" removed: pointless to the agent and the
        // window already falls back to the CRM details (Christian Gravias,
        // 13 Oct 2026). Only the positive confirmation is worth showing.
        <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-2xl border border-gray-100">
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
          <span className="text-sm font-bold text-gray-700">Verified Portal Account Connected</span>
          <span className="ml-auto text-xs text-gray-400">Edits update this buyer's portal preferences &amp; our stats</span>
        </div>
      ) : null}

      {!bulk && (
        <div className="p-4 rounded-2xl border border-amber-200 bg-amber-50/60" data-testid="fill-from-notes">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <p className="flex-1 text-[13px] text-gray-600">
              Mentioned a budget, suburbs or must-haves in your notes? Fill them in from the notes, then check and save.
            </p>
            <button
              type="button"
              onClick={fillFromNotes}
              disabled={!!noteFill?.reading}
              className="shrink-0 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl border border-amber-300 bg-white text-amber-700 text-[13px] font-bold hover:bg-amber-50 disabled:opacity-60 transition-colors"
            >
              {noteFill?.reading ? <Loader2 size={15} className="animate-spin" /> : <StickyNote size={15} />}
              {noteFill?.reading ? 'Reading notes…' : 'Fill from notes'}
            </button>
          </div>
          {noteFill?.changes && (
            <ul className="mt-3 space-y-1 text-[13px] text-gray-700">
              {noteFill.changes.map((c) => (
                <li key={c.field}>
                  <span className="font-bold">{c.label}:</span> {c.current} → <span className="font-bold text-[#00838F]">{c.suggested}</span>
                </li>
              ))}
              <li className="pt-1 text-[12px] font-semibold text-amber-700">Filled in below, not saved yet. Check them, then Save Preferences.</li>
            </ul>
          )}
        </div>
      )}

      {/* Property Type */}
      <div className="space-y-3">
        <label className="text-[16px] font-bold text-gray-900 block">Property Type</label>
        <p className="text-[12px] text-gray-500 -mt-2">Select one or more property types</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {PROPERTY_TYPES.map((type) => {
            const isSelected = form.propertyType.includes(type.id);
            const Icon = type.icon;
            return (
              <button
                key={type.id}
                type="button"
                onClick={() => togglePropertyType(type.id)}
                className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border transition-all w-full hover:shadow-md ${
                  isSelected
                    ? 'bg-[#E0F7FA] border-[#55C5D0] shadow-sm'
                    : 'bg-white border-gray-300 hover:border-[#55C5D0]/50 hover:bg-gray-50'
                }`}
              >
                <div className={`p-1.5 rounded-full ${isSelected ? 'bg-[#55C5D0]/20 text-[#00838F]' : 'bg-gray-100 text-gray-500'}`}>
                  <Icon size={18} />
                </div>
                <span className={`text-[13px] font-bold ${isSelected ? 'text-[#00838F]' : 'text-gray-700'}`}>{type.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <hr className="border-gray-100" />

      {/* Preferred Suburbs */}
      <div className="space-y-5">
        <div>
          <label className="text-[18px] font-bold text-gray-900 block">Preferred Suburbs</label>
          <p className="text-[13px] text-gray-500 font-medium mt-1">
            Add specific suburbs for the best property matches. Cities can be added if you prefer a broader search.
          </p>
        </div>

        <div className="relative" ref={suburbBoxRef}>
          <div className="flex items-stretch gap-2">
            <input
              type="text"
              value={suburbInput}
              onChange={(e) => onSuburbType(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSuburb(); } }}
              onFocus={() => suburbSuggestions.length > 0 && setSuburbOpen(true)}
              placeholder="Search for a specific suburb..."
              autoComplete="off"
              className="flex-1 min-w-0 h-[48px] px-4 rounded-xl border border-gray-300 bg-white text-[14px] font-medium placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/10 focus:border-[#55C5D0] transition-all"
            />
            <button
              type="button"
              onClick={() => addSuburb()}
              className="shrink-0 bg-[#55C5D0] hover:bg-[#45B5C0] text-white h-[48px] px-4 sm:px-6 rounded-xl font-bold text-[14px] transition-all flex items-center gap-1.5 shadow-sm"
            >
              <Plus size={18} /> Add
            </button>
          </div>
          {suburbOpen && suburbSuggestions.length > 0 && (
            <div className="absolute z-30 w-full mt-1.5 bg-white rounded-xl border border-gray-200 shadow-xl max-h-60 overflow-y-auto">
              {suburbSuggestions.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => addSuburb(s.name)}
                  className="w-full text-left px-4 py-2.5 hover:bg-[#F0FDFE] border-b border-gray-100 last:border-0 flex items-center gap-2.5"
                >
                  <span className="p-1 bg-gray-100 rounded">
                    {s.isManual ? <Plus className="w-3 h-3 text-[#55C5D0]" /> : <Map className="w-3 h-3 text-gray-500" />}
                  </span>
                  <span className="text-[13px] text-gray-800 font-medium">
                    {s.isManual ? `Add "${s.name}"` : s.name}
                  </span>
                  {!s.isManual && (
                    <span className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 uppercase tracking-wide">
                      {s.type}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <p className="text-[13px] font-bold text-gray-500">Popular Suburbs</p>
          <div className="flex flex-wrap gap-2">
            {POPULAR_SUBURBS.map((s) => (
              <Chip key={s} label={s} selected={form.suburbs.includes(s)} onClick={() => addSuburb(s)} />
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-[13px] font-bold text-gray-500">Or select a city</p>
          <div className="flex flex-wrap gap-2">
            {POPULAR_CITIES.map((c) => (
              <Chip key={c} label={c} selected={form.suburbs.includes(c)} onClick={() => addSuburb(c)} />
            ))}
          </div>
        </div>

        {form.suburbs.length > 0 && (
          <div className="flex flex-wrap gap-2.5">
            {form.suburbs.map((s, i) => (
              <div key={i} className="inline-flex items-center gap-2.5 px-4 py-2 bg-[#C9F9FF] text-black text-[13px] font-bold rounded-lg border border-[#55C5D0]">
                {s}
                <button type="button" onClick={() => removeSuburb(s)} className="hover:scale-110 transition-transform">
                  <X size={14} strokeWidth={3} />
                </button>
              </div>
            ))}
          </div>
        )}
        <p className="text-[13px] text-gray-500 font-medium">
          Add suburbs first for the best matches. You can add multiple locations — higher priority locations are matched first.
        </p>
      </div>

      <hr className="border-gray-100" />

      {/* Price Range */}
      <div className="space-y-4">
        <h3 className="text-[18px] font-bold text-gray-900">Price Range</h3>
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-1.5">
            <p className="text-[13px] font-bold text-gray-500">Min</p>
            <Dropdown name="priceMin" value={form.priceMin} options={PRICE_MIN_OPTIONS} onChange={handleChange} />
          </div>
          <div className="space-y-1.5">
            <p className="text-[13px] font-bold text-gray-500">Max</p>
            <Dropdown name="priceMax" value={form.priceMax} options={PRICE_MAX_OPTIONS} onChange={handleChange} />
          </div>
        </div>
      </div>

      <hr className="border-gray-100" />

      {/* Floor Area (Commercial) vs Bedrooms/Bathrooms/Car Spaces (Residential) */}
      {isCommercial ? (
        <div className="space-y-4">
          <h3 className="text-[18px] font-bold text-gray-900">Floor Area</h3>
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-1.5">
              <p className="text-[13px] font-bold text-gray-500">Min</p>
              <Dropdown name="floorAreaMin" value={form.floorAreaMin} options={FLOOR_AREA_OPTIONS} onChange={handleChange} />
            </div>
            <div className="space-y-1.5">
              <p className="text-[13px] font-bold text-gray-500">Max</p>
              <Dropdown name="floorAreaMax" value={form.floorAreaMax} options={FLOOR_AREA_OPTIONS} onChange={handleChange} />
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Bedrooms */}
          <div className="space-y-4">
            <h3 className="text-[18px] font-bold text-gray-900">Bedrooms</h3>
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-1.5">
                <p className="text-[13px] font-bold text-gray-500">Min</p>
                <Dropdown name="bedroomsMin" value={form.bedroomsMin} options={BED_OPTIONS} onChange={handleChange} />
              </div>
              <div className="space-y-1.5">
                <p className="text-[13px] font-bold text-gray-500">Max</p>
                <Dropdown name="bedroomsMax" value={form.bedroomsMax} options={BED_OPTIONS} onChange={handleChange} />
              </div>
            </div>
          </div>

          {/* Bathrooms & Car Spaces */}
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-[18px] font-bold text-gray-900">Bathrooms</h3>
              <Dropdown name="bathrooms" value={form.bathrooms} options={BATH_OPTIONS} onChange={handleChange} />
            </div>
            <div className="space-y-4">
              <h3 className="text-[18px] font-bold text-gray-900">Car Spaces</h3>
              <Dropdown name="carSpaces" value={form.carSpaces} options={CAR_OPTIONS} onChange={handleChange} />
            </div>
          </div>
        </>
      )}

      {/* Land Size */}
      <div className="space-y-4">
        <h3 className="text-[18px] font-bold text-gray-900">Land Size</h3>
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-1.5">
            <p className="text-[13px] font-bold text-gray-500">Min</p>
            <Dropdown name="landSizeMin" value={form.landSizeMin} options={LAND_OPTIONS} onChange={handleChange} />
          </div>
          <div className="space-y-1.5">
            <p className="text-[13px] font-bold text-gray-500">Max</p>
            <Dropdown name="landSizeMax" value={form.landSizeMax} options={LAND_OPTIONS} onChange={handleChange} />
          </div>
        </div>
      </div>

      <hr className="border-gray-100" />

      {/* Timeline */}
      <div className="space-y-4">
        <label className="text-[18px] font-bold text-gray-900 block">When are you looking to buy?</label>
        <Dropdown name="buyingTimeline" value={form.buyingTimeline} options={TIMELINE_OPTIONS} onChange={handleChange} />
      </div>

      <hr className="border-gray-100" />

      {/* Property Goals */}
      <div className="space-y-4">
        <h3 className="text-[18px] font-bold text-gray-900">Property Goals <span className="font-normal text-gray-500 ml-1.5">(Optional)</span></h3>
        <p className="text-[13px] text-gray-500 font-medium -mt-2">What is most important to them?</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {INVESTMENT_GOAL_OPTIONS.map((opt) => (
            <CheckOption
              key={opt.id}
              checked={form.investmentGoals.includes(opt.id)}
              onChange={() => toggleInArray('investmentGoals', opt.id)}
              label={opt.label}
            />
          ))}
        </div>
      </div>

      <hr className="border-gray-100" />

      {/* Buyer Context */}
      <div className="space-y-5">
        <h3 className="text-[18px] font-bold text-gray-900">Buyer Context <span className="font-normal text-gray-500 ml-1.5">(Optional)</span></h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100 space-y-5">
            <label className="text-[15px] font-bold text-gray-900 block">What best describes them?</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4">
              {BUYER_TYPE_OPTIONS.map((opt) => (
                <CheckOption
                  key={opt.id}
                  checked={form.buyerType.includes(opt.id)}
                  onChange={() => toggleInArray('buyerType', opt.id)}
                  label={opt.label}
                />
              ))}
            </div>
          </div>
          <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100 space-y-5">
            <label className="text-[15px] font-bold text-gray-900 block">Finance status</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4">
              {FINANCE_OPTIONS.map((opt) => (
                <CheckOption
                  key={opt}
                  checked={form.financeStatus.includes(opt)}
                  onChange={() => toggleInArray('financeStatus', opt)}
                  label={opt}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Save */}
      <div className="pt-2 flex justify-end gap-3 sticky bottom-0 bg-white py-4 -mx-4 px-4 sm:-mx-8 sm:px-8 border-t border-gray-100">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-8 py-3 bg-[#55C5D0] text-white rounded-xl text-sm font-bold shadow-md hover:bg-[#45B5C0] flex items-center gap-2 disabled:opacity-50"
        >
          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saveLabel || 'Save Preferences'}
        </button>
      </div>
    </div>
  );
};

export default ContactBuyerPreferencesEditor;
